
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import MediaFile from '@/repositories/data/MediaFile';
import { mediaEndpoint } from '@/container';

// tslint:disable-next-line:no-var-requires
const plyrIconUrl = require('@/assets/images/plyr.svg');

@Component
export default class MediaFileVideoPlayer extends Vue {
    @Prop({ type: Object, required: true }) public readonly file!: MediaFile;
    @Prop({ type: Object, required: false, default: null }) public readonly glossId!: string | null;
    @Prop({ type: Object, required: false, default: null }) public readonly variantId!: string | null;
    @Prop({ type: Boolean, required: false, default: false }) public readonly disableFullscreen!: boolean;
    @Prop({ type: Boolean, required: false, default: false }) public readonly fullscreenMedia!: boolean;
    @Prop({ type: Boolean, required: false, default: false }) public readonly loop!: boolean;
    @Prop({ type: Boolean, required: false, default: false }) public readonly immediately!: boolean;
    @Prop({ type: String, required: false, default: null }) public readonly playingFileId!: string | null;
    private didEnterFullscreen: boolean = false;

    public onPlayerPausedCallback = () => {
        this.onPausedPlaying();
    };

    public onPlayerPlayingCallback = () => {
        this.onStartedPlaying();
    };

    public onEndedPlayingCallback = () => {
        this.onEndedPlaying();
    };

    public onPlayerEnterFullscreenCallback = () => {
        this.onFullscreenEntered();
    };

    public onPlayerExitFullscreenCallback = () => {
        this.onFullscreenExited();
    };

    get options(): any {
        const controls: string[] = ['play-large', 'play', 'progress', 'settings', 'foobar'];
        if (!this.disableFullscreen) {
            controls.push('fullscreen');
        }
        return {
            controls,
            settings: ['speed', 'loop'],
            disableContextMenu: false,
            autoplay: this.immediately,
            muted: this.immediately,
            loop: { active: this.loop },
            resetOnEnd: true,
            ratio: '16:9',
            speed: { selected: 1, options: [0.25, 0.5, 0.75, 1] }, // 0.25 doesn't work
            hideControls: false,
            iconUrl: plyrIconUrl,
        };
    }

    public get mediaQueryParameter(): string {
        if (this.variantId) {
            return `variant=${this.variantId}`;
        } else if (this.glossId) {
            return `gloss=${this.glossId}`;
        } else {
            return '';
        }
    }

    public get poster(): string | null {
        return `${mediaEndpoint}/media/thumb/${this.file.id}?${this.mediaQueryParameter}`;
    }

    public get sources(): { src: string; type: string }[] {
        return [
            {
                src: `${mediaEndpoint}/media/film/${this.file.id}?${this.mediaQueryParameter}`,
                type: 'video/mp4',
            },
        ];
    }

    public get player(): any {
        const plyr: any = this.$refs.plyr;
        return plyr.player;
    }

    public mounted(): void {
        this.$nextTick(() => {
            this.player.on('playing', this.onPlayerPlayingCallback);
            this.player.on('pause', this.onPlayerPausedCallback);
            this.player.on('ended', this.onEndedPlayingCallback);
            this.player.on('enterfullscreen', this.onPlayerEnterFullscreenCallback);
            this.player.on('exitfullscreen', this.onPlayerExitFullscreenCallback);
        });
    }

    public updated(): void {
        this.$nextTick(() => {
            this.player.on('playing', this.onPlayerPlayingCallback);
            this.player.on('pause', this.onPlayerPausedCallback);
            this.player.on('ended', this.onEndedPlayingCallback);
            this.player.on('enterfullscreen', this.onPlayerEnterFullscreenCallback);
            this.player.on('exitfullscreen', this.onPlayerExitFullscreenCallback);
        });
    }

    public beforeDestroy(): void {
        this.player.off('playing', this.onPlayerPlayingCallback);
        this.player.off('pause', this.onPlayerPausedCallback);
        this.player.off('ended', this.onEndedPlayingCallback);
        this.player.off('enterfullscreen', this.onPlayerEnterFullscreenCallback);
        this.player.off('exitfullscreen', this.onPlayerExitFullscreenCallback);
    }

    @Watch('sources')
    private onSourcesChanged() {
        this.player.source = {
            type: 'video',
            sources: this.sources,
            poster: this.poster,
        };
    }

    @Watch('fullscreenMedia', { immediate: true })
    private onFullscreenMediaChanged(value: boolean): void {
        if (this.didEnterFullscreen && !value) {
            this.$nextTick(() => {
                this.player.fullscreen.exit();
            });
        }
    }

    @Watch('playingFileId', { immediate: true })
    private onPlayingChanged(value: string | null) {
        if (value === null) {
            if (this.immediately) {
                this.$emit('update:playingFileId', this.file.id?.toString());
            }
        } else if (value.toString() === this.file.id?.toString()) {
            this.$nextTick(() => {
                if (this.immediately && this.loop) {
                    this.player.currentTime = 0;
                }
                this.player.play();
            });
        } else {
            this.$nextTick(() => {
                this.player.pause();
            });
        }
    }

    private onStartedPlaying() {
        this.$emit('update:playingFileId', this.file.id?.toString());
        this.$emit('startedPlaying');
    }

    private onPausedPlaying() {
        this.$emit('pausedPlaying');
    }

    private onEndedPlaying() {
        // Warning: as per plyr documentation, does not fire if playback started through autoplay
        this.$emit('endedPlaying');
    }

    private onFullscreenEntered() {
        this.didEnterFullscreen = true;
        this.$emit('update:fullscreenMedia', true);
    }

    private onFullscreenExited() {
        if (this.didEnterFullscreen) {
            this.didEnterFullscreen = false;
            this.$emit('update:fullscreenMedia', false);
        }
    }
}
